import { parseISO, differenceInMinutes, subMonths, subYears, differenceInDays } from 'date-fns';

// Mirror analyticsUtils API, but source from Redux arrays provided by caller

export function filterByPeriod(date, selectedPeriod = '7 derniers jours') {
  const now = new Date();
  switch (selectedPeriod) {
    case "Aujourd'hui":
      return differenceInMinutes(now, date) <= 1440;
    case '7 derniers jours':
      return differenceInMinutes(now, date) <= 10080;
    case '30 derniers jours':
      return differenceInMinutes(now, date) <= 43200;
    case '3 derniers mois':
      return date >= subMonths(now, 3);
    case '1 dernière année':
      return date >= subYears(now, 1);
    default:
      return true;
  }
}

export function calculateSessionStatsFromRedux(sessions = [], selectedPeriod = '7 derniers jours', email = '') {
  console.log('[Backend Analytics] Raw sessions count:', sessions?.length);
  console.log('[Backend Analytics] Sample session:', sessions?.[0]);
  console.log('[Backend Analytics] All sessions endedAt values:', sessions?.slice(0, 5).map(s => ({ id: s._id || s.sessionId, endedAt: s.endedAt, startedAt: s.startedAt })));
  
  // Since you only send ENDED sessions to backend, we should expect endedAt to exist
  // But let's check both cases to debug the issue
  const withEndedAt = (sessions || []).filter(s => s?.startedAt && s?.endedAt);
  const withoutEndedAt = (sessions || []).filter(s => s?.startedAt && !s?.endedAt);
  
  console.log('[Backend Analytics] Sessions WITH endedAt:', withEndedAt.length);
  console.log('[Backend Analytics] Sessions WITHOUT endedAt:', withoutEndedAt.length);
  
  // Use the sessions that have endedAt (completed sessions)
  const filtered = withEndedAt.filter(s => {
    const start = safeParseISO(s.startedAt);
    if (!start) return false;
    
    // If email is empty/whitespace, show ALL users (no email filtering)
    const matchesEmail = !email?.trim() || String(s.email || s.userId || '').toLowerCase().includes(email.trim().toLowerCase());
    const matchesPeriod = filterByPeriod(start, selectedPeriod);
    
    console.log('[Backend Analytics] Session filter:', { 
      sessionEmail: s.email || s.userId, 
      filterEmail: email?.trim(), 
      matchesEmail, 
      matchesPeriod,
      startDate: s.startedAt,
      endDate: s.endedAt,
      hasEndedAt: !!s.endedAt
    });
    
    return matchesEmail && matchesPeriod;
  });
  
  console.log('[Backend Analytics] Filtered sessions count:', filtered.length);

  if (filtered.length === 0) {
    return {
      averageDuration: 0,
      totalSessions: 0,
      totalTime: 0,
      shortestSession: 0,
      longestSession: 0,
      sessionLengths: [],
      averageActivityTime: 0,
      averageInactivityTime: 0,
      totalActivityTime: 0,
      totalInactivityTime: 0,
      activityTimes: [],
      inactivityTimes: []
    };
  }

  const sessionLengths = filtered.map(session => {
    const start = safeParseISO(session.startedAt);
    const end = safeParseISO(session.endedAt); // Should always exist for ended sessions
    
    if (!start || !end) {
      console.warn('[Backend Analytics] Invalid session dates:', { 
        sessionId: session._id || session.sessionId,
        startedAt: session.startedAt, 
        endedAt: session.endedAt 
      });
      return 0;
    }
    
    const minutes = differenceInMinutes(end, start);
    return Math.round(Math.max(0, minutes));
  });

  console.log('[Backend Analytics] Session lengths calculated:', sessionLengths.slice(0, 5));

  const activityTimes = filtered.map(s => Math.round(s.activity_duration || 0));
  const inactivityTimes = filtered.map(s => Math.round(s.inactivity_duration || 0));

  const totalTime = sessionLengths.reduce((a, b) => a + b, 0);
  const totalActivityTime = activityTimes.reduce((a, b) => a + b, 0);
  const totalInactivityTime = inactivityTimes.reduce((a, b) => a + b, 0);

  return {
    averageDuration: Math.round(totalTime / filtered.length),
    totalSessions: filtered.length,
    totalTime: Math.round(totalTime),
    shortestSession: Math.round(Math.min(...sessionLengths)),
    longestSession: Math.round(Math.max(...sessionLengths)),
    sessionLengths,
    averageActivityTime: Math.round(totalActivityTime / filtered.length),
    averageInactivityTime: Math.round(totalInactivityTime / filtered.length),
    totalActivityTime: Math.round(totalActivityTime),
    totalInactivityTime: Math.round(totalInactivityTime),
    activityTimes,
    inactivityTimes
  };
}

export function calculateToolUsagePercentageFromRedux(events = [], space = 'personal', selectedPeriod = '7 derniers jours', email = '') {
  console.log('[Backend Analytics] Raw events count:', events?.length);
  console.log('[Backend Analytics] Sample event:', events?.[0]);

  // Map UI space to backend sessionType
  const spaceToSessionType = {
    personal: 'personal_space',
    project: 'project_space',
    manager: 'manager_space'
  };
  const wantedType = spaceToSessionType[space] || 'personal_space';

  // Filter by email (if provided), space, and period using event.timestamp
  const filtered = (events || []).filter(evt => {
    const emailMatch = !email?.trim() || String(evt.email || evt.userId || '').toLowerCase().includes(email.trim().toLowerCase());
    const typeMatch = (evt.sessionType || '').toLowerCase() === wantedType.toLowerCase();
    const ts = safeParseISO(evt.timestamp);
    const periodMatch = ts ? filterByPeriod(ts, selectedPeriod) : false;
    return emailMatch && typeMatch && periodMatch;
  });

  // Extract tool name from details.tool (fallbacks included)
  const tools = filtered
    .map(evt => (evt?.details?.tool || evt?.tool || evt?.details?.toolName || null))
    .filter(t => typeof t === 'string' && t.length > 0);

  console.log('[Backend Analytics] Tools found for space', space, ':', tools.length);

  const totalUsage = tools.length;
  if (totalUsage === 0) return [];

  const counts = tools.reduce((acc, t) => {
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts).map(([tool, count]) => ({
    tool,
    percentage: ((count / totalUsage) * 100).toFixed(2)
  }));
}

// Primary source for KPIs: compute directly from per-session tool bundles
export function calculateToolUsagePercentageFromBundles(bundles = [], space = 'personal', selectedPeriod = '7 derniers jours', email = '') {
  try {
    const mapSpace = {
      personal: 'personalTools',
      project: 'projectTools',
      manager: 'managerTools'
    };
    const arrName = mapSpace[space] || 'personalTools';

    const items = (bundles || [])
      .filter(b => {
        // Email filter: empty/whitespace means ALL
        const emailMatch = !email?.trim() || String(b.userEmail || b.email || b.userId || '').toLowerCase().includes(email.trim().toLowerCase());
        return emailMatch;
      })
      .flatMap(b => (Array.isArray(b[arrName]) ? b[arrName].map(t => ({
        tool: t?.tool || t?.name || t,
        timestamp: t?.timestamp || b.datetime || b.endedAt || b.startedAt
      })) : []))
      .filter(it => it.tool && typeof it.tool === 'string')
      .filter(it => {
        const d = safeParseISO(it.timestamp);
        return d ? filterByPeriod(d, selectedPeriod) : false;
      });

    const total = items.length;
    if (total === 0) return [];

    const counts = items.reduce((acc, it) => {
      acc[it.tool] = (acc[it.tool] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts).map(([tool, count]) => ({
      tool,
      percentage: ((count / total) * 100).toFixed(2)
    }));
  } catch (e) {
    console.error('[Backend Analytics] calculateToolUsagePercentageFromBundles error:', e);
    return [];
  }
}

export function countProjectsFromRedux(projects = [], selectedPeriod = '7 derniers jours', email = '') {
  console.log('[Backend Analytics] Raw projects count:', projects?.length);
  
  const filteredByOwner = (projects || []).filter(p => {
    // If email is empty/whitespace, show ALL users (no email filtering)
    if (!email?.trim()) return true;
    const owner = p?.ownerEmail || p?.owner || p?.createdBy || '';
    return String(owner).toLowerCase().includes(email.trim().toLowerCase());
  });

  const withDates = filteredByOwner.map(p => ({ p, d: getProjectCreationDate(p) })).filter(x => x.p);
  const validDates = withDates.filter(x => x.d);
  // If no projects have a parseable date, fall back to simple count (ignore period)
  if (validDates.length === 0) return filteredByOwner.length;
  
  const result = validDates.filter(x => filterByPeriod(x.d, selectedPeriod)).length;
  console.log('[Backend Analytics] Projects after filtering:', result);
  return result;
}

export function safeParseISO(val) {
  try {
    const d = typeof val === 'string' ? parseISO(val) : (val instanceof Date ? val : null);
    return d && !isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
}

function getProjectCreationDate(project) {
  const candidates = [
    project?.creationDate,
    project?.createdAt,
    project?.modificationDate?.date || project?.modificationDate,
    project?.updatedAt,
    project?.date,
  ];
  for (const c of candidates) {
    const d = safeParseISO(c) || (c?.$date ? safeParseISO(c.$date) : null);
    if (d) return d;
  }
  if (project?.seconds) {
    const d = new Date(project.seconds * 1000);
    if (!isNaN(d.getTime())) return d;
  }
  if (project?.toDate && typeof project.toDate === 'function') {
    const d = project.toDate();
    if (d instanceof Date && !isNaN(d.getTime())) return d;
  }
  return null;
}

export function calculateUsersStatsFromSessions(sessions = [], projects = [], selectedPeriod = '7 derniers jours', emailFilter = '', userObj = null) {
  try {
    // Filter sessions by period and email
    const filteredSessions = (sessions || []).filter(s => {
      if (!s?.startedAt || !s?.endedAt) return false;
      
      const start = safeParseISO(s.startedAt);
      if (!start) return false;
      
      const matchesEmail = !emailFilter?.trim() || String(s.email || s.userId || '').toLowerCase().includes(emailFilter.trim().toLowerCase());
      const matchesPeriod = filterByPeriod(start, selectedPeriod);
      
      return matchesEmail && matchesPeriod;
    });
    
    // Group sessions by user
    const userStats = {};
    const now = new Date();

    filteredSessions.forEach(session => {
      const userEmail = session.email || session.userId || 'Unknown';
      
      if (!userStats[userEmail]) {
        userStats[userEmail] = {
          email: userEmail,
          sessions: [],
          lastActivityDate: null
        };
      }
      
      userStats[userEmail].sessions.push(session);
      
      // Track last activity
      const sessionStart = safeParseISO(session.startedAt);
      if (sessionStart && (!userStats[userEmail].lastActivityDate || sessionStart > userStats[userEmail].lastActivityDate)) {
        userStats[userEmail].lastActivityDate = sessionStart;
      }
    });

  // Calculate stats for each user
    const result = Object.values(userStats).map(user => {
      const sessionCount = user.sessions.length;
      const daysSinceLastActivity = user.lastActivityDate ? differenceInDays(now, user.lastActivityDate) : Infinity;
      
      // Calculate frequency based on sessions in period
      let frequency = 'Inactif';
      if (sessionCount > 0) {
        const periodDays = getPeriodDays(selectedPeriod);
        const sessionsPerWeek = (sessionCount / periodDays) * 7;
        
        if (sessionsPerWeek >= 7) frequency = 'Daily';
        else if (sessionsPerWeek >= 5) frequency = '5x/semaine';
        else if (sessionsPerWeek >= 3) frequency = '3x/semaine';
        else if (sessionsPerWeek >= 1) frequency = '1x/semaine';
        else frequency = 'Occasionnel';
      }
      
      // Count projects for this user
      let projectsCount = 0;
      const userEmailLower = String(user.email || '').toLowerCase();
      if (userObj && typeof userObj === 'object' && !Array.isArray(userObj)) {
        // Prefer backend-provided userObj: { email: { projectId: timestampString?, ... }, ... }
        // Find the exact key (case-insensitive)
        let rec = userObj[user.email] || userObj[userEmailLower] || null;
        if (!rec) {
          // Try to find by lower-casing keys if necessary
          try {
            const entries = Object.entries(userObj);
            const found = entries.find(([k]) => String(k).toLowerCase() === userEmailLower);
            if (found) rec = found[1];
          } catch {}
        }
        if (rec && typeof rec === 'object') {
          // For the users table KPI, show total projects linked to the user (no period filter)
          projectsCount = Object.keys(rec).length;
        } else {
          projectsCount = 0;
        }
      } else {
        // Fallback: infer from projects array by email
        const userProjects = (projects || []).filter(project => {
          const projectEmail = project.email || project.userEmail || project.userId || '';
          return String(projectEmail).toLowerCase() === userEmailLower;
        });
        projectsCount = userProjects.length;
      }
      
      // Determine status
      const status = daysSinceLastActivity <= 7 ? 'Actif' : 'Inactif';
      
      return {
        user: user.email,
        frequency,
        projects: projectsCount,
        status,
        sessionCount,
        lastActivity: user.lastActivityDate
      };
    }).sort((a, b) => {
      // Sort by status (Actif first), then by session count descending
      if (a.status !== b.status) {
        return a.status === 'Actif' ? -1 : 1;
      }
      return b.sessionCount - a.sessionCount;
    });

    // Ensure we always return an array
    return Array.isArray(result) ? result : [];
  } catch (error) {
    console.error('[Backend Analytics] Error in calculateUsersStatsFromSessions:', error);
    return [];
  }
}

function getPeriodDays(selectedPeriod) {
  switch (selectedPeriod) {
    case "Aujourd'hui": return 1;
    case '7 derniers jours': return 7;
    case '30 derniers jours': return 30;
    case '3 derniers mois': return 90;
    case '1 dernière année': return 365;
    default: return 7;
  }
}
